
/**
 * DraftCard — single draft editor (hook / body / cta / hashtags) with
 * channel target, schedule, asset preview/download, and the powerful
 * AI Refine bar.
 *
 * Used by:
 *   · /portal/content-calendar          (deprecated slide-over; phased out)
 *   · /portal/content-calendar/plan/... (new full-screen planner)
 *
 * Also exports the small style/label maps so the planner can render
 * status chips and channel labels consistently.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Copy, Check, Trash2, Sparkles, Send, SkipForward, XCircle, CheckCircle2 } from "lucide-react";
import { MicButton } from "../MicButton";
import { VisualStudio } from "./VisualStudio";
import { useBuffer, channelsForAngle } from "./BufferContext";
import DraftCopilot from "./DraftCopilot";

const API = process.env.REACT_APP_BACKEND_URL;

export const STATUS_COLOURS = {
  idea:      { bg: "rgba(12,42,64,0.06)",     border: "rgba(12,42,64,0.30)",  text: "rgba(12,42,64,0.65)" },
  drafted:   { bg: "rgba(194,146,46,0.10)",   border: "rgba(194,146,46,0.40)", text: "#8A6914" },
  scheduled: { bg: "rgba(63,128,191,0.10)",   border: "rgba(63,128,191,0.40)", text: "#1E4E7A" },
  posted:    { bg: "rgba(31,182,193,0.10)",   border: "rgba(31,182,193,0.40)", text: "#0E7A82" },
  skipped:   { bg: "rgba(184,92,56,0.10)",    border: "rgba(184,92,56,0.40)",  text: "#B85C38" },
};

export const ANGLE_LABELS = {
  "linkedin-text":     "LinkedIn — text",
  "linkedin-carousel": "LinkedIn — carousel",
  "linkedin-video":    "LinkedIn — video script",
  instagram:           "Instagram",
  facebook:            "Facebook",
  youtube:             "YouTube",
  tiktok:              "TikTok",
};

export const SOURCE_LABELS = {
  insight:  "Article",
  video:    "Video",
  resource: "PDF",
  tool:     "Tool",
};

export const isImagePath = (s) =>
  typeof s === "string" &&
  /\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i.test(s.trim());

export const absImage = (s) => {
  const t = (s || "").trim();
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith("/")) return `${window.location.origin}${t}`;
  return t;
};

// Auto-save status pill — appears in the card header so editors can see
// every keystroke is being persisted ("saved 14:32"). Sticky but quiet.
function AutosaveIndicator({ state, savedAt, error }) {
  if (state === "idle") return null;
  let label = "Autosave";
  let bg = "rgba(12,42,64,0.06)";
  let border = "rgba(12,42,64,0.18)";
  let color = "rgba(12,42,64,0.6)";
  if (state === "pending") {
    label = "Editing…";
    bg = "rgba(194,146,46,0.10)"; border = "rgba(194,146,46,0.35)"; color = "#8A6914";
  } else if (state === "saving") {
    label = "Saving…";
    bg = "rgba(63,128,191,0.10)"; border = "rgba(63,128,191,0.35)"; color = "#1E4E7A";
  } else if (state === "saved") {
    const t = savedAt ? new Date(savedAt) : new Date();
    label = `Saved ${t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    bg = "rgba(31,182,193,0.12)"; border = "rgba(31,182,193,0.40)"; color = "#0E7A82";
  } else if (state === "error") {
    label = `Autosave failed — ${error || "retry by editing again"}`;
    bg = "rgba(184,92,56,0.12)"; border = "rgba(184,92,56,0.40)"; color = "#B85C38";
  }
  return (
    <span
      className="px-2 py-0.5 font-mono uppercase"
      style={{ background: bg, border: `1px solid ${border}`,
               color, fontSize: "0.56rem",
               letterSpacing: "0.16em", fontWeight: 700, borderRadius: 3,
               whiteSpace: "nowrap" }}
      data-testid="cc-autosave-indicator"
    >
      {label}
    </span>
  );
}


export function Field({ label, children, action }) {
  return (
    <div className="mb-2.5">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="font-mono uppercase"
             style={{ fontSize: "0.58rem", letterSpacing: "0.22em",
                      color: "rgba(12,42,64,0.55)" }}>
          {label}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export function Pill({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="px-2.5 py-1 font-mono uppercase"
      style={{
        background: active ? "#1FB6C1" : "transparent",
        color: active ? "#0C2A40" : "rgba(12,42,64,0.65)",
        border: `1px solid ${active ? "#1FB6C1" : "rgba(12,42,64,0.25)"}`,
        fontSize: "0.6rem", letterSpacing: "0.16em",
        fontWeight: 700, cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

export function DraftCard({ draft, onUpdate, onAutosave, onDelete, apiToken, extraAssets = [], onReload, channelKey }) {
  const [hook, setHook]       = useState(draft.hook || "");
  const [body, setBody]       = useState(draft.body || "");
  const [cta, setCta]         = useState(draft.cta || "");
  const [tags, setTags]       = useState((draft.hashtags || []).join(" "));
  const [target, setTarget]   = useState(draft.target_channel || "personal");
  const [status, setStatus]   = useState(draft.status || "drafted");
  const [scheduledFor, setScheduledFor] = useState(draft.scheduled_for || "");
  const [copied, setCopied]   = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [autosaveState, setAutosaveState] = useState("idle"); // 'idle' | 'pending' | 'saving' | 'saved' | 'error'
  const [autosaveError, setAutosaveError] = useState("");

  // Powerful refine — user can ask for polish, tone shifts, full rewrites,
  // or "make this about X angle instead". Claude is instructed to obey the
  // editor's intent while still respecting the 3-lane brand voice.
  const [refineInstr, setRefineInstr] = useState("");
  const [refining, setRefining] = useState(false);
  const [refineError, setRefineError] = useState("");
  const [undoSnap, setUndoSnap] = useState(null);

  const isLinkedIn = (draft.angle || "").startsWith("linkedin");
  // Target channel selector only makes sense on LinkedIn (personal vs company).
  // For IG/FB/YT/TikTok the channel IS the angle, so we hide it.
  const showTargetSelector = isLinkedIn;

  // ── Autosave (debounced, silent) ──────────────────────────────────
  // Every edit to hook/body/cta/tags/target/status/scheduledFor schedules
  // a silent PATCH 1.5 s after the user stops typing. No reload, no
  // scroll jump — just a tiny "saved 14:32" indicator near the section
  // headers so the editor knows their work is persisted.
  const mountedRef = useRef(false);
  const skipNextAutosave = useRef(true); // skip the very first render
  const saveTimerRef = useRef(null);

  useEffect(() => {
    // First render: capture the initial values without firing a save.
    // Ref mutations are deferred to a microtask to avoid synchronous writes
    // during render.
    if (skipNextAutosave.current) {
      const initT = window.setTimeout(() => {
        skipNextAutosave.current = false;
        mountedRef.current = true;
      }, 0);
      return () => window.clearTimeout(initT);
    }
    if (!onAutosave || !draft.id) return undefined;

    let cleanedUp = false;
    const localPendingTimer = window.setTimeout(() => {
      if (cleanedUp) return;
      setAutosaveState("pending");
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        if (cleanedUp) return;
        setAutosaveState("saving");
        setAutosaveError("");
        try {
          const patch = {
            hook, body, cta,
            hashtags: (tags || "").split(/\s+/).filter(Boolean).map((t) => t.replace(/^#/, "")),
            target_channel: target,
            status,
            scheduled_for: scheduledFor || null,
            revision_source: "autosave",
          };
          await onAutosave(draft.id, patch);
          if (cleanedUp) return;
          setSavedAt(Date.now());
          setAutosaveState("saved");
        } catch (e) {
          if (cleanedUp) return;
          setAutosaveState("error");
          setAutosaveError(e.message || "autosave failed");
        }
      }, 1500);
    }, 0);

    return () => {
      cleanedUp = true;
      window.clearTimeout(localPendingTimer);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hook, body, cta, tags, target, status, scheduledFor]);

  // Flush any pending autosave when the card unmounts (tab change /
  // navigation) so a last-second edit is never lost.
  useEffect(() => {
    return () => {
      if (!saveTimerRef.current || !onAutosave || !draft.id) return;
      clearTimeout(saveTimerRef.current);
      // Fire-and-forget: keystroke before unmount must be persisted.
      const patch = {
        hook, body, cta,
        hashtags: (tags || "").split(/\s+/).filter(Boolean).map((t) => t.replace(/^#/, "")),
        target_channel: target,
        status,
        scheduled_for: scheduledFor || null,
      };
      onAutosave(draft.id, patch).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const full = useMemo(() => {
    const tagLine = (tags || "").trim();
    const tagPart = tagLine
      ? tagLine.split(/\s+/).map((t) => (t.startsWith("#") ? t : `#${t}`)).join(" ")
      : "";
    return [hook, "", body, "", cta, tagPart && "", tagPart]
      .filter((x) => x !== false).join("\n").trim();
  }, [hook, body, cta, tags]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const save = async () => {
    await onUpdate(draft.id, {
      hook, body, cta,
      hashtags: (tags || "").split(/\s+/).filter(Boolean).map((t) => t.replace(/^#/, "")),
      target_channel: target,
      status,
      scheduled_for: scheduledFor || null,
    });
    setSavedAt(Date.now());
  };

  const refine = async (instruction = "", mode = "auto") => {
    setRefining(true);
    setRefineError("");
    setUndoSnap({ hook, body, cta, tags });
    try {
      const r = await fetch(
        `${API}/api/content-calendar/drafts/${draft.id}/refine`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${apiToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            instruction: instruction || null,
            mode,                  // "auto" | "polish" | "rewrite"
            current_hook: hook,
            current_body: body,
            current_cta: cta,
            current_hashtags: (tags || "")
              .split(/\s+/).filter(Boolean).map((t) => t.replace(/^#/, "")),
          }),
        }
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Could not refine");
      const ref = data.refined || {};
      if (ref.hook !== undefined) setHook(ref.hook);
      if (ref.body !== undefined) setBody(ref.body);
      if (ref.cta !== undefined)  setCta(ref.cta);
      if (Array.isArray(ref.hashtags)) setTags(ref.hashtags.join(" "));
      setRefineInstr("");
    } catch (e) {
      setRefineError(e.message);
    } finally {
      setRefining(false);
    }
  };

  const undoRefine = () => {
    if (!undoSnap) return;
    setHook(undoSnap.hook);
    setBody(undoSnap.body);
    setCta(undoSnap.cta);
    setTags(undoSnap.tags);
    setUndoSnap(null);
  };

  const c = STATUS_COLOURS[status] || STATUS_COLOURS.drafted;

  // Combine the draft's suggested_image (if it's a file path) with any
  // channel-specific extra assets passed in by the planner.
  const seedImage = isImagePath(draft.suggested_image) ? draft.suggested_image : null;
  const gallery = useMemo(() => {
    const items = [];
    if (seedImage && !extraAssets.includes(seedImage)) items.push(seedImage);
    for (const a of extraAssets) items.push(a);
    // Surface custom assets (Studio output) at the end of the gallery so
    // the seeded launch-week tiles still appear first.
    for (const a of (draft.custom_assets || [])) {
      if (a.url && !items.includes(a.url)) items.push(a.url);
    }
    return items;
  }, [seedImage, extraAssets, draft.custom_assets]);

  return (
    <div className="hv-card p-5 space-y-5"
         data-testid={`cc-draft-${draft.id}`}>
      {/* ═══════════════════════════════ Card header ═══════════════════════════════ */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono uppercase"
               style={{ fontSize: "0.6rem", letterSpacing: "0.20em",
                        color: "#0E7A82", fontWeight: 700 }}>
            {ANGLE_LABELS[draft.angle] || draft.angle}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <AutosaveIndicator state={autosaveState} savedAt={savedAt} error={autosaveError} />
          <BufferStatusPill draft={draft} apiToken={apiToken} />
          <span className="px-2 py-0.5 font-mono uppercase"
                style={{ background: c.bg, border: `1px solid ${c.border}`,
                         color: c.text, fontSize: "0.58rem",
                         letterSpacing: "0.18em", fontWeight: 700, borderRadius: 3 }}>
            {status}
          </span>
        </div>
      </div>

      {/* Quick lifecycle action strip — Skip · Cancel · Mark posted.
          Each non-destructive action triggers a backwards cascade so the
          calendar gap closes (later non-posted drafts shift back one
          Mon/Wed/Fri slot, Buffer schedules updated in lock-step). */}
      <LifecycleActions
        draft={draft}
        apiToken={apiToken}
        status={status}
        setStatus={setStatus}
        scheduledFor={scheduledFor}
        onReload={onReload}
      />

      {/* ═══════════════════════════════ TWO-COLUMN BODY ═══════════════════════════════
          Left  · Text (hook · body · CTA · co-pilot chat & history)
          Right · Visual (gallery · Visual Studio)
          Layout lives in .cc-draft-grid (index.css) — class-only so the
          @media (max-width: 1023px) rule can stack to one column on tablets/
          phones. NO inline grid-template-columns (would beat the media query
          due to CSS specificity). */}
      <div className="cc-draft-grid">
        {/* ─── LEFT · TEXT ─────────────────────────────────────────── */}
      <Section title="Text" subtitle="Hook · body · CTA · AI co-pilot" testid={`cc-section-text-${draft.id}`}>
        <Field
          label="Hook (first line — must stop the scroll)"
          action={<MicButton value={hook} onChange={setHook} apiToken={apiToken}
                            testid={`cc-mic-hook-${draft.id}`} />}
        >
        <textarea
          value={hook} onChange={(e) => setHook(e.target.value)} rows={2}
          className="w-full px-2 py-1.5 outline-none"
          style={{ background: "#FAFAF7", border: "1px solid rgba(12,42,64,0.20)", borderRadius: 6,
                   fontSize: "0.9rem", color: "#0C2A40", fontFamily: "inherit",
                   resize: "vertical" }}
          data-testid={`cc-hook-${draft.id}`}
        />
      </Field>
      <Field
        label="Body"
        action={<MicButton value={body} onChange={setBody} apiToken={apiToken}
                          testid={`cc-mic-body-${draft.id}`} />}
      >
        <textarea
          value={body} onChange={(e) => setBody(e.target.value)} rows={8}
          className="w-full px-2 py-1.5 outline-none"
          style={{ background: "#FAFAF7", border: "1px solid rgba(12,42,64,0.20)", borderRadius: 6,
                   fontSize: "0.9rem", color: "#0C2A40", fontFamily: "inherit",
                   resize: "vertical", lineHeight: 1.55 }}
          data-testid={`cc-body-${draft.id}`}
        />
      </Field>
      <Field
        label="CTA (closing line)"
        action={<MicButton value={cta} onChange={setCta} apiToken={apiToken}
                          testid={`cc-mic-cta-${draft.id}`} />}
      >
        <input
          type="text" value={cta} onChange={(e) => setCta(e.target.value)}
          className="w-full px-2 py-1.5 outline-none"
          style={{ background: "#FAFAF7", border: "1px solid rgba(12,42,64,0.20)", borderRadius: 6,
                   fontSize: "0.9rem", color: "#0C2A40" }}
          data-testid={`cc-cta-${draft.id}`}
        />
      </Field>

      {/* Co-pilot — chat + history rail, context behind an info icon.
          Lives in the LEFT column so the editor can iterate copy while
          looking at the visual on the right. */}
      <div className="mt-4 p-4"
           style={{ background: "rgba(31,182,193,0.06)",
                    border: "1px solid rgba(31,182,193,0.30)", borderRadius: 10 }}>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={14} style={{ color: "#0E7A82" }} />
          <span className="font-mono uppercase"
                style={{ fontSize: "0.62rem", letterSpacing: "0.20em",
                         color: "#0E7A82", fontWeight: 700 }}>
            HidroVerse co-pilot — chat · history
          </span>
        </div>
        <DraftCopilot
          draft={draft}
          apiToken={apiToken}
          hook={hook}    setHook={setHook}
          body={body}    setBody={setBody}
          cta={cta}      setCta={setCta}
          tags={tags}    setTags={setTags}
          onApplied={() => { /* parent refresh handled via autosave reload */ }}
        />
      </div>
      </Section>

      {/* ─── RIGHT · VISUAL ──────────────────────────────────────── */}
      <Section title="Visual" subtitle="Generated assets · upload · re-prompt · compose with overlay" testid={`cc-section-visual-${draft.id}`}>
      {/* Asset gallery — channel-aware. Show every applicable platform size. */}
      {gallery.length > 0 && (
        <Field label={`Visual assets · ${gallery.length} ready`}>
          <div className="grid grid-cols-2 gap-3"
               data-testid={`cc-gallery-${draft.id}`}>
            {gallery.map((p) => (
              <AssetTile key={p} path={p} draftId={draft.id} hook={hook} />
            ))}
          </div>
        </Field>
      )}
      {gallery.length === 0 && draft.suggested_image && !isImagePath(draft.suggested_image) && (
        <Field label="Suggested visual">
          <div className="font-mono px-2 py-1.5"
               style={{ background: "#FAFAF7", border: "1px solid rgba(12,42,64,0.10)",
                        fontSize: "0.78rem", color: "rgba(12,42,64,0.7)",
                        borderRadius: 6 }}>
            {draft.suggested_image}
          </div>
        </Field>
      )}

      {/* Visual Studio (Design / Upload / From website / Edit overlays) */}
      <VisualStudio draft={draft} apiToken={apiToken} onAttached={onReload}
                    hook={hook} />
      </Section>
      </div>

      {/* ═══════════════════════════════ FULL-WIDTH · PUBLISH ═══════════════════════════════ */}
      <Section title="Publish" subtitle="Hashtags · status · schedule · actions" testid={`cc-section-publish-${draft.id}`}>
        <Field
          label="Hashtags"
          action={<MicButton value={tags} onChange={setTags} apiToken={apiToken}
                            testid={`cc-mic-tags-pub-${draft.id}`} />}
        >
          {/* Chip editor — every preset hashtag is a removable chip. Click ×
              to delete. Type into the field then press Enter / space / comma
              to add. Backspace on empty input removes the last chip. */}
          <HashtagChips value={tags} onChange={setTags} draftId={draft.id} />
          <HashtagLibrary
            channel={draft.angle}
            currentTags={tags}
            onAppend={(picks) => {
              const have = new Set(
                (tags || "").split(/\s+/).filter(Boolean).map((t) => t.replace(/^#/, "").toLowerCase()),
              );
              const adds = picks.filter((p) => !have.has(p.toLowerCase()));
              const next = [tags.trim(), adds.join(" ")].filter(Boolean).join(" ").trim();
              setTags(next);
            }}
            onReplace={(picks) => setTags(picks.join(" "))}
            draftId={draft.id}
          />
        </Field>

        {showTargetSelector && (
          <Field label="Target channel">
            <div className="flex gap-2">
              <Pill active={target === "personal"} onClick={() => setTarget("personal")} testid={`cc-target-personal-${draft.id}`}>Personal</Pill>
              <Pill active={target === "company"}  onClick={() => setTarget("company")}  testid={`cc-target-company-${draft.id}`}>HV page</Pill>
            </div>
          </Field>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <Field label="Status">
            <select
              value={status} onChange={(e) => setStatus(e.target.value)}
              className="w-full px-2 py-1.5 outline-none"
              style={{ background: "#FAFAF7", border: "1px solid rgba(12,42,64,0.20)", borderRadius: 6,
                       fontSize: "0.9rem", color: "#0C2A40", cursor: "pointer" }}
              data-testid={`cc-status-${draft.id}`}
            >
              <option value="drafted">Drafted</option>
              <option value="scheduled">Scheduled</option>
              <option value="posted">Posted</option>
              <option value="skipped">Skipped</option>
            </select>
          </Field>
          <Field label="Scheduled for (optional)">
            <input
              type="date" value={scheduledFor || ""}
              onChange={(e) => setScheduledFor(e.target.value)}
              className="w-full px-2 py-1.5 outline-none"
              style={{ background: "#FAFAF7", border: "1px solid rgba(12,42,64,0.20)", borderRadius: 6,
                       fontSize: "0.9rem", color: "#0C2A40", fontFamily: "inherit" }}
              data-testid={`cc-scheduled-${draft.id}`}
            />
          </Field>
        </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 mt-4">
        <button
          type="button"
          onClick={copy}
          data-testid={`cc-copy-${draft.id}`}
          className="hv-pill"
          style={copied ? { background: "#0E7A82", color: "#FFFFFF", borderColor: "#0E7A82" } : undefined}
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
          {copied ? "Copied" : "Copy text"}
        </button>
        <button type="button" onClick={save}
                data-testid={`cc-save-${draft.id}`}
                className="hv-pill is-active">
          Save
        </button>
        {savedAt && (
          <span className="font-mono uppercase self-center"
                style={{ fontSize: "0.6rem", letterSpacing: "0.16em",
                         color: "rgba(12,42,64,0.55)" }}>
            saved
          </span>
        )}
        <div className="flex-1" />
        <BufferPushButton draft={draft} channelKey={channelKey}
                          apiToken={apiToken} onPushed={onReload} />
        <button type="button"
                onClick={() => onDelete(draft.id)}
                data-testid={`cc-delete-${draft.id}`}
                className="hv-pill is-danger">
          <Trash2 size={11} />
        </button>
      </div>
      </Section>
    </div>
  );
}

/* Buffer status pill — shows the live state of a draft that has been
   pushed to Buffer. Reads buffer_post_id from the draft row; polls
   /api/buffer/post-status every 90s to flip from 'scheduled' to
   'sent' once Buffer publishes. Stays hidden if the draft has never
   been pushed. */
function BufferStatusPill({ draft, apiToken }) {
  const initialDue = draft.buffer_due_at || null;
  const initialState = draft.buffer_post_id
    ? (draft.buffer_sent_at ? "sent" : "scheduled")
    : null;
  const [state, setState] = useState(initialState);
  const [dueAt, setDueAt] = useState(initialDue);
  const [sentAt, setSentAt] = useState(draft.buffer_sent_at || null);

  useEffect(() => {
    // Defer the sync set-state-trio so react-hooks/set-state-in-effect
    // doesn't trip on the synchronous chain.
    const t = window.setTimeout(() => {
      const initial = draft.buffer_post_id
        ? (draft.buffer_sent_at ? "sent" : "scheduled")
        : null;
      setState(initial);
      setDueAt(draft.buffer_due_at || null);
      setSentAt(draft.buffer_sent_at || null);
    }, 0);
    return () => window.clearTimeout(t);
  }, [draft.buffer_post_id, draft.buffer_due_at, draft.buffer_sent_at]);

  useEffect(() => {
    if (!draft.buffer_post_id) return undefined;
    if (state === "sent") return undefined;        // already final — stop polling

    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch(
          `${API}/api/buffer/post-status/${draft.buffer_post_id}`,
          { headers: { Authorization: `Bearer ${apiToken}` } },
        );
        if (!r.ok) return;
        const j = await r.json();
        if (cancelled) return;
        if (j.status === "sent") {
          setState("sent");
          setSentAt(j.sent_at || new Date().toISOString());
        } else if (j.status && j.status !== "unknown") {
          setState(j.status);
          if (j.due_at) setDueAt(j.due_at);
        }
      } catch { /* ignore — pill stays in its previous state */ }
    };
    poll();                                        // first check immediately
    const t = setInterval(poll, 90 * 1000);        // then every 90 s
    return () => { cancelled = true; clearInterval(t); };
  }, [draft.buffer_post_id, apiToken, state]);

  if (!state) return null;

  const tone = {
    sent:      { bg: "rgba(31,182,193,0.18)", fg: "#0E7A82", label: "✓ Posted" },
    scheduled: { bg: "rgba(63,128,191,0.15)", fg: "#1E4E7A", label: "↗ Scheduled" },
    failed:    { bg: "rgba(184,92,56,0.15)",  fg: "#B85C38", label: "✗ Failed" },
  }[state] || { bg: "rgba(63,128,191,0.15)", fg: "#1E4E7A", label: state };

  const when = state === "sent"
    ? (sentAt ? new Date(sentAt).toLocaleString() : "")
    : (dueAt ? new Date(dueAt).toLocaleString() : "");

  return (
    <span
      className="px-2 py-0.5 font-mono uppercase"
      style={{ background: tone.bg, color: tone.fg,
               fontSize: "0.58rem", letterSpacing: "0.18em",
               fontWeight: 700, borderRadius: 3 }}
      title={when || ""}
      data-testid={`cc-buffer-status-${draft.id}`}
    >
      Buffer {tone.label}{when ? ` · ${when}` : ""}
    </span>
  );
}

/* Push-to-Buffer button — appears in every draft's PUBLISH actions row.
   Channel mapping uses the BufferContext: e.g. an "instagram" angle
   draft routes to the IG channel; "linkedin-*" routes to a LinkedIn
   channel. If multiple LinkedIn channels exist (Page + personal), the
   user picks via the small dropdown that appears next to the button. */
function BufferPushButton({ draft, channelKey, apiToken, onPushed }) {
  const { ready, status, channels } = useBuffer();
  const eligible = channelsForAngle(channels, draft.angle);
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const target = picked || eligible[0]?.id || "";

  if (!ready) return null;
  if (!status?.connected) return null;
  if (eligible.length === 0) return null;

  const push = async () => {
    if (!target) return;
    const which = eligible.find((c) => c.id === target);
    if (!window.confirm(
      `Push this ${draft.angle} draft to Buffer (${which?.name} / ${which?.service})?\n\n` +
      "It will be added to the Buffer queue (next available slot).",
    )) return;
    setBusy(true); setMsg("");
    try {
      const r = await fetch(`${API}/api/buffer/push-draft`, {
        method: "POST",
        headers: { Authorization: `Bearer ${apiToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ draft_id: draft.id, channel_id: target }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Push failed");
      if (j.ok) {
        setMsg("Pushed");
        onPushed && onPushed();
      } else {
        setMsg(j.error || "Buffer rejected");
      }
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(""), 6000);
    }
  };

  return (
    <div className="flex items-center gap-1 flex-wrap"
         data-testid={`cc-buffer-push-${draft.id}`}>
      {eligible.length > 1 && (
        <select value={target}
                onChange={(e) => setPicked(e.target.value)}
                disabled={busy}
                data-testid={`cc-buffer-channel-${draft.id}`}
                className="hv-input"
                style={{ fontSize: "0.72rem", padding: "3px 6px",
                         maxWidth: 200 }}>
          {eligible.map((c) => (
            <option key={c.id} value={c.id}>{c.name} ({c.service})</option>
          ))}
        </select>
      )}
      <button type="button"
              onClick={push}
              disabled={busy || !target}
              data-testid={`cc-buffer-push-btn-${draft.id}`}
              className="hv-pill is-active"
              style={{ background: "#0E7A82", color: "#FFFFFF",
                       borderColor: "#0E7A82" }}>
        <Send size={11} />
        {busy ? "Pushing…" : "Push to Buffer"}
      </button>
      {msg && (
        <span className="font-mono uppercase"
              style={{ fontSize: "0.6rem", letterSpacing: "0.16em",
                       color: msg === "Pushed" ? "#0E7A82" : "#B85C38" }}>
          {msg}
        </span>
      )}
    </div>
  );
}

/* Section wrapper — 3 clearly-separated stages per draft: Text · Visual · Publish */
function Section({ title, subtitle, testid, children }) {
  return (
    <section
      className="hv-card-soft"
      style={{ padding: 16 }}
      data-testid={testid}
    >
      <header className="mb-3 pb-2"
              style={{ borderBottom: "1px dashed rgba(12,42,64,0.12)" }}>
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div className="font-mono uppercase"
               style={{ fontSize: "0.62rem", letterSpacing: "0.24em",
                        color: "#0E7A82", fontWeight: 700 }}>
            {title}
          </div>
          {subtitle && (
            <div className="font-light"
                 style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.55)" }}>
              {subtitle}
            </div>
          )}
        </div>
      </header>
      {children}
    </section>
  );
}

const HASHTAG_LIBRARY = {
  brand:  ["HidroVerse", "WaterEngineering"],
  geo:    ["SouthAfrica", "Mpumalanga", "Nelspruit", "Lowveld"],
  topics: {
    "Borehole / pump":   ["BoreholeWater", "PumpSizing", "BoreholeMaintenance"],
    "RO / purification": ["ReverseOsmosis", "WaterTreatment", "SafeDrinkingWater", "SANS241"],
    "Engineering tools": ["EngineeringTools", "WaterInfrastructure", "EngineeringDesign"],
    "Irrigation / agri": ["IrrigationDesign", "AgribusinessSA"],
    "Lodge sector":      ["SafariLodge", "LodgeOperations", "LodgeManagement"],
    "Mining sector":     ["MiningSA", "MiningEngineering"],
    "Municipal sector":  ["MunicipalEngineering"],
  },
};

// Channel-specific count guidance
const HASHTAG_RECOMMENDATIONS = {
  "linkedin-text":     { min: 3, max: 5, label: "LinkedIn · 3–5 hashtags" },
  "linkedin-carousel": { min: 3, max: 5, label: "LinkedIn · 3–5 hashtags" },
  "linkedin-video":    { min: 3, max: 5, label: "LinkedIn · 3–5 hashtags" },
  instagram:           { min: 5, max: 8, label: "Instagram · 5–8 hashtags" },
  facebook:            { min: 1, max: 3, label: "Facebook · 1–3 hashtags" },
};

function HashtagLibrary({ channel, currentTags, onAppend, draftId }) {
  const rec = HASHTAG_RECOMMENDATIONS[channel] || HASHTAG_RECOMMENDATIONS["linkedin-text"];
  const have = new Set(
    (currentTags || "").split(/\s+/).filter(Boolean)
      .map((t) => t.replace(/^#/, "").toLowerCase()),
  );
  const currentCount = have.size;

  // colour the counter chip green if inside target range, amber otherwise
  const inRange = currentCount >= rec.min && currentCount <= rec.max;
  const counterColour = inRange ? "#0E7A82" : "#8A6914";
  const counterBg     = inRange ? "rgba(31,182,193,0.10)" : "rgba(194,146,46,0.10)";

  return (
    <div className="mt-2 hv-card-soft"
         style={{ padding: 10 }}
         data-testid={`cc-hashtag-library-${draftId}`}>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="font-mono uppercase"
             style={{ fontSize: "0.58rem", letterSpacing: "0.22em",
                      color: "rgba(12,42,64,0.55)", fontWeight: 700 }}>
          Hashtag library — click to add
        </div>
        <span className="font-mono uppercase"
              style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                       background: counterBg, color: counterColour,
                       padding: "3px 8px", borderRadius: 999, fontWeight: 700 }}>
          {currentCount} of {rec.label}
        </span>
      </div>

      <Row title="Brand" tags={HASHTAG_LIBRARY.brand} have={have}
           onPick={(t) => onAppend([t])} draftId={draftId} />
      <Row title="Geographic" tags={HASHTAG_LIBRARY.geo} have={have}
           onPick={(t) => onAppend([t])} draftId={draftId} />
      {Object.entries(HASHTAG_LIBRARY.topics).map(([title, arr]) => (
        <Row key={title} title={title} tags={arr} have={have}
             onPick={(t) => onAppend([t])} draftId={draftId} />
      ))}
    </div>
  );
}

function Row({ title, tags, have, onPick, draftId }) {
  return (
    <div className="flex items-baseline gap-2 mb-1.5 flex-wrap">
      <div className="font-mono uppercase shrink-0"
           style={{ fontSize: "0.55rem", letterSpacing: "0.18em",
                    color: "rgba(12,42,64,0.50)", fontWeight: 700,
                    minWidth: 110 }}>
        {title}
      </div>
      <div className="flex flex-wrap gap-1">
        {tags.map((t) => {
          const picked = have.has(t.toLowerCase());
          return (
            <button
              key={t}
              type="button"
              onClick={() => onPick(t)}
              disabled={picked}
              data-testid={`cc-hashtag-${draftId}-${t}`}
              className="font-mono"
              style={{
                background: picked ? "rgba(31,182,193,0.18)" : "#FFFFFF",
                color: picked ? "rgba(12,42,64,0.50)" : "#0C2A40",
                border: `1px solid ${picked ? "rgba(31,182,193,0.40)" : "rgba(12,42,64,0.18)"}`,
                fontSize: "0.65rem", padding: "2px 8px", borderRadius: 999,
                cursor: picked ? "default" : "pointer", fontWeight: 500,
                opacity: picked ? 0.7 : 1,
              }}
              title={picked ? "Already added" : "Click to add"}
            >
              #{t}{picked ? " ✓" : ""}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* Asset tile — image preview + format label + size + download button.
 *
 * Decodes filename patterns into human-readable platform labels so the
 * editor knows at a glance which image goes where (Story, Reel cover,
 * Feed Portrait, Link share, etc.).
 */
const FORMAT_LABELS = {
  "li-1200x627":   { label: "LinkedIn Feed",       sub: "Link / Wide post · 1.91:1" },
  "li-1200x1200":  { label: "LinkedIn Square",     sub: "Native square post · 1:1" },
  "ig-1080x1080":  { label: "Instagram Feed",      sub: "Square post · 1:1" },
  "ig-1080x1350":  { label: "Instagram Portrait",  sub: "Feed portrait · 4:5" },
  "story-1080x1920": { label: "Story / Reel cover", sub: "IG · LinkedIn · FB · 9:16" },
};

function decodeAssetFormat(filename) {
  // Match prefix like "launch-li-1200x627" → strip leading brief slug
  for (const [key, meta] of Object.entries(FORMAT_LABELS)) {
    if (filename.includes(key)) return { ...meta, key };
  }
  // Fallback — show the raw dimensions
  const dims = (filename.match(/(\d+x\d+)/) || [])[1];
  return { label: "Asset", sub: dims || filename, key: dims || filename };
}

function AssetTile({ path, draftId }) {
  const filename = path.split("/").pop();
  const url = absImage(path);
  const fmt = decodeAssetFormat(filename);
  const dims = (filename.match(/(\d+x\d+)/) || [])[1] || "";

  return (
    <div className="hv-card-soft is-hoverable"
         style={{ padding: 10, transition: "transform 200ms ease" }}
         onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-1px)")}
         onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}>
      <a href={url} target="_blank" rel="noreferrer"
         style={{ display: "block", lineHeight: 0 }}>
        <img src={url} alt={fmt.label} loading="lazy"
             style={{ width: "100%", height: 130, objectFit: "contain",
                      background: "#16181B",
                      border: "1px solid rgba(12,42,64,0.06)",
                      borderRadius: 8 }} />
      </a>

      <div className="mt-2 px-0.5">
        <div className="font-mono uppercase"
             style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                      color: "#0E7A82", fontWeight: 700 }}>
          {fmt.label}
        </div>
        <div className="mt-0.5"
             style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.70)",
                      lineHeight: 1.3 }}>
          {fmt.sub}
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-1">
          <span className="font-mono"
                style={{ fontSize: "0.6rem", color: "rgba(12,42,64,0.50)" }}>
            {dims}
          </span>
          <a href={url} download
             data-testid={`cc-asset-download-${draftId}-${dims || filename}`}
             className="font-mono uppercase"
             style={{ background: "#1FB6C1", color: "#0C2A40",
                      fontSize: "0.55rem", letterSpacing: "0.14em",
                      fontWeight: 700, textDecoration: "none",
                      padding: "3px 10px", borderRadius: 999 }}>
            Download
          </a>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * HashtagChips — chip editor used in the publish-channel form. Replaces
 * the legacy space-separated text input so the marketer can remove ANY
 * tag (preset or not) by clicking the × on its chip. Typed text commits
 * on Enter / space / comma. Backspace on an empty input removes the
 * last chip.
 * ────────────────────────────────────────────────────────────────────── */
function HashtagChips({ value, onChange, draftId }) {
  const list = (value || "").split(/\s+/).filter(Boolean).map((t) => t.replace(/^#/, ""));
  const [buf, setBuf] = useState("");
  const commit = (raw) => {
    const cleaned = raw.replace(/^#/, "").replace(/[^A-Za-z0-9_]/g, "");
    if (!cleaned) return;
    if (list.some((t) => t.toLowerCase() === cleaned.toLowerCase())) return;
    onChange([...list, cleaned].join(" "));
  };
  const removeAt = (idx) => {
    onChange(list.filter((_, i) => i !== idx).join(" "));
  };
  return (
    <div className="w-full px-2 py-1.5 flex flex-wrap items-center gap-1.5"
         style={{ background: "#FAFAF7", border: "1px solid rgba(12,42,64,0.20)",
                  borderRadius: 6, minHeight: 38 }}
         data-testid={`cc-tags-chips-${draftId}`}>
      {list.map((t, i) => (
        <span key={`${t}-${i}`}
              className="inline-flex items-center gap-1 px-2 py-0.5"
              style={{ background: "#1FB6C1", color: "#0C2A40",
                       borderRadius: 999, fontSize: "0.78rem",
                       fontFamily: "monospace", fontWeight: 600 }}
              data-testid={`cc-tag-chip-${draftId}-${t.toLowerCase()}`}>
          #{t}
          <button type="button"
                  onClick={() => removeAt(i)}
                  data-testid={`cc-tag-remove-${draftId}-${t.toLowerCase()}`}
                  className="inline-flex items-center justify-center"
                  style={{ width: 14, height: 14, lineHeight: 1,
                           background: "rgba(12,42,64,0.18)",
                           color: "#0C2A40", borderRadius: 999,
                           border: "none", cursor: "pointer",
                           fontSize: "0.7rem", fontWeight: 700, padding: 0 }}
                  aria-label={`Remove ${t}`}>×</button>
        </span>
      ))}
      <input type="text" value={buf}
             placeholder={list.length === 0 ? "Type a tag, press Enter" : "+ add"}
             onChange={(e) => setBuf(e.target.value)}
             onKeyDown={(e) => {
               if (e.key === "Enter" || e.key === " " || e.key === ",") {
                 e.preventDefault(); commit(buf); setBuf("");
               } else if (e.key === "Backspace" && buf === "" && list.length > 0) {
                 e.preventDefault(); removeAt(list.length - 1);
               }
             }}
             onBlur={() => { if (buf.trim()) { commit(buf); setBuf(""); } }}
             data-testid={`cc-tags-input-${draftId}`}
             style={{ flex: "1 1 80px", minWidth: 80, background: "transparent",
                      border: "none", outline: "none", fontSize: "0.85rem",
                      color: "#0C2A40", fontFamily: "monospace", padding: "2px 4px" }} />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * LifecycleActions — Skip · Cancel · Mark posted, each followed by a
 * backwards cascade so the calendar gap closes automatically.
 *
 *   · Skip       → status="skipped". The slot remains visible on the
 *                  calendar with the skipped chip, but later non-posted
 *                  drafts pull back one Mon/Wed/Fri to fill the gap.
 *   · Cancel     → soft-delete (deleted_at set). Same backwards cascade.
 *   · Mark posted→ status="posted" + posted_at = today. Useful when the
 *                  editor publishes manually outside Buffer and wants the
 *                  remaining queue to reshuffle.
 *
 * Cascade calls POST /api/content-calendar/cascade-pull with from_date =
 * the draft's scheduled_for and source_slug_to_skip set so the action'd
 * draft itself doesn't get pulled.
 * ────────────────────────────────────────────────────────────────────── */
function LifecycleActions({ draft, apiToken, status, setStatus, scheduledFor, onReload }) {
  const [busy, setBusy] = useState("");
  const [msg, setMsg]   = useState("");

  const cascade = async (fromDate, slug) => {
    if (!fromDate) return null;
    try {
      const r = await fetch(`${API}/api/content-calendar/cascade-pull`, {
        method: "POST",
        headers: { Authorization: `Bearer ${apiToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ from_date: fromDate, source_slug_to_skip: slug }),
      });
      const j = await r.json();
      return j;
    } catch (e) {
      return { ok: false, error: e.message };
    }
  };

  const doSkip = async () => {
    if (!window.confirm(
      "Skip this draft?\n\nIt stays visible (marked SKIPPED) but every later non-posted draft will pull back one Mon/Wed/Fri slot to close the gap.",
    )) return;
    setBusy("skip"); setMsg("");
    try {
      const r = await fetch(`${API}/api/content-calendar/drafts/${draft.id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${apiToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status: "skipped", revision_source: "lifecycle-skip" }),
      });
      if (!r.ok) throw new Error("Failed to skip");
      setStatus("skipped");
      const c = await cascade(scheduledFor || draft.scheduled_for, draft.source_slug);
      const n = (c?.shifted || []).length;
      setMsg(n > 0 ? `Skipped · ${n} slot${n === 1 ? "" : "s"} pulled back` : "Skipped");
      if (onReload) onReload();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(""); setTimeout(() => setMsg(""), 6000);
    }
  };

  const doCancel = async () => {
    if (!window.confirm(
      "Cancel & remove this draft?\n\nThe draft is soft-deleted (recoverable from Trash) and every later non-posted draft pulls back one Mon/Wed/Fri slot to close the gap.",
    )) return;
    setBusy("cancel"); setMsg("");
    try {
      // Cascade FIRST (using the now-vacant slot), then soft-delete.
      const c = await cascade(scheduledFor || draft.scheduled_for, draft.source_slug);
      const r = await fetch(`${API}/api/content-calendar/drafts/${draft.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      if (!r.ok) throw new Error("Failed to cancel");
      const n = (c?.shifted || []).length;
      setMsg(n > 0 ? `Cancelled · ${n} slot${n === 1 ? "" : "s"} pulled back` : "Cancelled");
      if (onReload) onReload();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(""); setTimeout(() => setMsg(""), 6000);
    }
  };

  const doMarkPosted = async () => {
    if (!window.confirm(
      "Mark this draft as posted?\n\nUse this when you've already published outside Buffer. The remaining non-posted queue will pull back one Mon/Wed/Fri slot to close the gap.",
    )) return;
    setBusy("posted"); setMsg("");
    try {
      const today = new Date().toISOString().slice(0, 10);
      const r = await fetch(`${API}/api/content-calendar/drafts/${draft.id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${apiToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          status: "posted",
          posted_at: today,
          revision_source: "lifecycle-mark-posted",
        }),
      });
      if (!r.ok) throw new Error("Failed to mark posted");
      setStatus("posted");
      const c = await cascade(scheduledFor || draft.scheduled_for, draft.source_slug);
      const n = (c?.shifted || []).length;
      setMsg(n > 0 ? `Marked posted · ${n} slot${n === 1 ? "" : "s"} pulled back` : "Marked posted");
      if (onReload) onReload();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(""); setTimeout(() => setMsg(""), 6000);
    }
  };

  // Hide lifecycle actions for already-posted drafts (no further state
  // to move it to) and for skipped drafts (they already free their slot).
  if (status === "posted") return null;

  return (
    <div className="flex flex-wrap items-center gap-2"
         style={{
           background: "rgba(63,128,191,0.05)",
           border: "1px dashed rgba(63,128,191,0.30)",
           borderRadius: 8, padding: "8px 10px",
         }}
         data-testid={`cc-lifecycle-${draft.id}`}>
      <span className="font-mono uppercase"
            style={{ fontSize: "0.56rem", letterSpacing: "0.20em",
                     color: "rgba(12,42,64,0.55)", fontWeight: 700 }}>
        Lifecycle
      </span>
      <button
        type="button" onClick={doSkip} disabled={busy !== "" || status === "skipped"}
        data-testid={`cc-lifecycle-skip-${draft.id}`}
        className="inline-flex items-center gap-1.5 px-3 py-1"
        style={{
          background: "#FFFFFF",
          border: "1px solid rgba(194,146,46,0.50)",
          color: "#8A6914",
          fontSize: "0.72rem", fontFamily: "ui-monospace, monospace",
          letterSpacing: "0.10em", textTransform: "uppercase", fontWeight: 600,
          borderRadius: 999, cursor: busy ? "wait" : "pointer",
          opacity: busy === "skip" ? 0.55 : (status === "skipped" ? 0.4 : 1),
        }}
      >
        <SkipForward size={11} />
        {busy === "skip" ? "Skipping…" : "Skip"}
      </button>
      <button
        type="button" onClick={doMarkPosted} disabled={busy !== ""}
        data-testid={`cc-lifecycle-mark-posted-${draft.id}`}
        className="inline-flex items-center gap-1.5 px-3 py-1"
        style={{
          background: "#FFFFFF",
          border: "1px solid rgba(31,182,193,0.55)",
          color: "#0E7A82",
          fontSize: "0.72rem", fontFamily: "ui-monospace, monospace",
          letterSpacing: "0.10em", textTransform: "uppercase", fontWeight: 600,
          borderRadius: 999, cursor: busy ? "wait" : "pointer",
          opacity: busy === "posted" ? 0.55 : 1,
        }}
      >
        <CheckCircle2 size={11} />
        {busy === "posted" ? "Marking…" : "Mark posted"}
      </button>
      <button
        type="button" onClick={doCancel} disabled={busy !== ""}
        data-testid={`cc-lifecycle-cancel-${draft.id}`}
        className="inline-flex items-center gap-1.5 px-3 py-1"
        style={{
          background: "#FFFFFF",
          border: "1px solid rgba(184,92,56,0.45)",
          color: "#B85C38",
          fontSize: "0.72rem", fontFamily: "ui-monospace, monospace",
          letterSpacing: "0.10em", textTransform: "uppercase", fontWeight: 600,
          borderRadius: 999, cursor: busy ? "wait" : "pointer",
          opacity: busy === "cancel" ? 0.55 : 1,
        }}
      >
        <XCircle size={11} />
        {busy === "cancel" ? "Cancelling…" : "Cancel"}
      </button>
      {msg && (
        <span className="font-mono uppercase"
              data-testid={`cc-lifecycle-msg-${draft.id}`}
              style={{ fontSize: "0.6rem", letterSpacing: "0.14em",
                       color: msg.toLowerCase().includes("fail") || msg.toLowerCase().includes("error")
                         ? "#B85C38" : "#0E7A82",
                       fontWeight: 700 }}>
          {msg}
        </span>
      )}
    </div>
  );
}

